import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessage, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import Guid from "_shared/guids/guid";
import { useLazyGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import type { PermissionJSON } from "permissions/_schemas/permission-schema";
import Permission from "permissions/permission";
import { useEffect, useState } from "react";

/** `PermissionQueriesController` rejects anything larger. */
const PAGE_SIZE = 100;

/**
 * Enough to cover the grantees an administrator realistically applies a template to
 * at once, without letting one click fan out into an unbounded run of requests. Past
 * it the preview says so rather than quietly under-reporting revocations.
 */
const MAX_PAGES = 12;

/**
 * Filtered to the template's resource types, the read is exactly the grants a template can touch —
 * a few dozen per person — so it is read to the end (#6175). The bound only stops a runaway.
 */
const MAX_FILTERED_PAGES = 50;

type FetchPermissionsPage = (args: { query: PermissionQuery }) => {
  unwrap: () => Promise<Paginated<PermissionJSON>>;
};

export type ActiveGranteePermissions = {
  permissions: Permission[];
  isLoading: boolean;
  error?: string;
  /** True when the grantees hold more permissions than the preview was willing to fetch. */
  isTruncated: boolean;
};

type LoadedPermissions = Pick<ActiveGranteePermissions, "permissions" | "isTruncated">;

async function loadActivePermissions(
  fetchPage: FetchPermissionsPage,
  granteeUserIdValues: string[],
  resourceTypes: string[],
): Promise<LoadedPermissions> {
  const granteeUserIds = granteeUserIdValues.map((value) => new Guid(value));
  const permissions: Permission[] = [];
  const filtered = resourceTypes.length > 0;
  const maxPages = filtered ? MAX_FILTERED_PAGES : MAX_PAGES;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex++) {
    const query = new PermissionQuery({
      permissionView: PermissionView.Flat,
      filterParameters: new PermissionFilterParameters({
        granteeUserIds,
        showDisabled: false,
        resourceTypes: filtered ? resourceTypes : undefined,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex, pageSize: PAGE_SIZE }),
    });

    const page = await fetchPage({ query }).unwrap();
    permissions.push(...Permission.fromJSONArray(page.data));

    // A missing or unparsable x-pagination header leaves totalPages at 0, which would
    // otherwise read as "already past the last page" and stop after a single full page.
    const { totalPages } = page.headers;
    const isLastPage = page.data.length < PAGE_SIZE || (totalPages > 0 && pageIndex + 1 >= totalPages);
    if (isLastPage) return { permissions, isTruncated: false };
  }

  return { permissions, isTruncated: true };
}

function pendingResult(granteeKey: string): ActiveGranteePermissions {
  return { permissions: [], isLoading: granteeKey.length > 0, isTruncated: false };
}

/**
 * Loads every active permission held by the given grantees, paging until the API runs
 * out of them. Disabled permissions are left out because the server ignores them too:
 * they neither satisfy a template grant nor get swept up by a resource-type replacement.
 *
 * Given `resourceTypes` — a template's — it reads `view=Flat&granteeUserIds=X&resourceTypes=…`,
 * exactly the grants applying the template can grant, keep or take away, so the preview misses none.
 */
export default function useActiveGranteePermissions(
  granteeUserIds: Guid[],
  resourceTypes: readonly string[] = [],
): ActiveGranteePermissions {
  const [fetchPermissionsPage] = useLazyGetPermissionsQuery();

  // A joined string keeps the effect from re-running for a fresh array holding the
  // same grantees, which is what the callers pass on every render.
  const granteeKey = granteeUserIds.map((granteeUserId) => granteeUserId.value).join(",");
  const resourceTypesKey = [...new Set(resourceTypes)].sort().join(",");

  const [result, setResult] = useState<ActiveGranteePermissions>(() => pendingResult(granteeKey));

  // Reset the working state during render rather than from the effect, so a changed
  // grantee set does not first paint the previous set's answer as if it were this one.
  const requestKey = `${granteeKey}|${resourceTypesKey}`;
  const [syncedRequestKey, setSyncedRequestKey] = useState(requestKey);
  if (syncedRequestKey !== requestKey) {
    setSyncedRequestKey(requestKey);
    setResult(pendingResult(granteeKey));
  }

  useEffect(() => {
    if (!granteeKey) return;

    let isCurrentRequest = true;

    loadActivePermissions(fetchPermissionsPage, granteeKey.split(","), resourceTypesKey ? resourceTypesKey.split(",") : [])
      .then((loaded) => {
        if (isCurrentRequest) setResult({ ...loaded, isLoading: false });
      })
      .catch((error: unknown) => {
        if (!isCurrentRequest) return;
        setResult({
          permissions: [],
          isLoading: false,
          isTruncated: false,
          error:
            getErrorMessage(error) ||
            getErrorMessageFromUnknown(error) ||
            "Unable to load the grantees' current permissions.",
        });
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [fetchPermissionsPage, granteeKey, resourceTypesKey]);

  return result;
}
